import 'twin.macro';
import React from 'react';
import Button from 'components/Buttons/Button';
import FormGroup from 'components/Form/FormGroup';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownCircleIcon,
  ChartBarIcon,
  PrinterIcon,
  QueueListIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { SidebarWithSections } from 'components/SubSidebar/SidebarWithSections';
import { Field, useFormState } from 'react-final-form';
import { FontsCoupleSelector } from 'components/Form/FontsCoupleSelector';
import { FontSizeSelector } from 'components/FontSizeSelector';
import { ColorSelector } from 'components/Form/ColorSelector';
import { BgImageSelectorWithImages } from 'components/Form/BgImageSelector/BgImageSelectorWithImages';
import { MarginsFieldWrapper } from 'components/Margins';
import { Pyramide, PyramidePDFSettings } from 'types/pyramide';
import InputGroup from 'components/Form/InputGroup';
import { FilterGroupsEleves } from 'containers/PyramidesDesAges/Pyramide/Sidebar/FilterGroupsEleves';
import Select from 'components/Form/Select2';
import ButtonGroup from 'components/Form/ButtonGroup';
import { Icon } from 'components/Icons/icon';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';

interface Props {
  limitedBgSet: boolean;
  onDownload: (settings: PyramidePDFSettings) => void;
  isDownloading: boolean;
  pyramide: Pyramide;
  groups: string[];
  selectedGroups: string[];
  setSelectedGroups: (v: string[]) => void;
  onUpdatePyramide: (v: Pyramide) => void;
}

export function Sidebar(props: Props) {
  const {
    isDownloading,
    onDownload,
    limitedBgSet,
    groups,
    selectedGroups,
    setSelectedGroups,
    onUpdatePyramide,
    pyramide,
  } = props;
  const { t } = useTranslation(['carnet', 'common', 'eleve']);
  const { values: settings } = useFormState<PyramidePDFSettings>();
  const RotatedChartBarIcon = p => <ChartBarIcon {...p} tw="rotate-90" />;
  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownCircleIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload(settings)}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownCircleIcon}
          loading={isDownloading}
          onClick={() => onDownload(settings)}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
      </div>

      <SidebarWithSections.Section
        icon={QueueListIcon}
        title={t('common:pdf.elementsAndDisposition')}
      >
        <FormGroup label={t('eleve:pyramide.titleLabel')}>
          <InputGroup
            value={pyramide.title}
            onChange={v => onUpdatePyramide({ ...pyramide, title: v })}
          />
        </FormGroup>
        <FormGroup label={t('eleve:pyramide.subtitleLabel')}>
          <InputGroup
            value={pyramide.subtitle || ''}
            onChange={v => onUpdatePyramide({ ...pyramide, subtitle: v })}
          />
        </FormGroup>
        <FormGroup label={t('eleve:pyramide.layoutLabel')}>
          <Field
            name="layout"
            render={({ input: { onChange, value } }) => (
              <ButtonGroup fill>
                <Button
                  icon={ChartBarIcon}
                  onClick={() => onChange('byMonths')}
                  active={value === 'byMonths' || !value}
                >
                  {t('eleve:pyramide.layoutByMonths')}
                </Button>
                <Button
                  icon={RotatedChartBarIcon as Icon}
                  onClick={() => onChange('byEleves')}
                  active={value === 'byEleves'}
                >
                  {t('eleve:pyramide.layoutByEleves')}
                </Button>
              </ButtonGroup>
            )}
          />
        </FormGroup>
        {settings.layout === 'byEleves' && (
          <FormGroup label={t('eleve:pyramide.nameFormatLabel')}>
            <Field
              name="formatNameStyle"
              render={({ input: { onChange, value } }) => (
                <Select
                  value={value || 'givenNameOnly'}
                  onChange={onChange}
                  options={[
                    {
                      value: 'givenNameOnly',
                      label: t('eleve:pyramide.nameFormat.givenNameOnly'),
                    },
                    {
                      value: 'familyNameOnly',
                      label: t('eleve:pyramide.nameFormat.familyNameOnly'),
                    },
                    {
                      value: 'givenNameAndFamilyName',
                      label: t(
                        'eleve:pyramide.nameFormat.givenNameAndFamilyName',
                      ),
                    },
                    {
                      value: 'givenNameAndFirstLetterFamilyName',
                      label: t(
                        'eleve:pyramide.nameFormat.givenNameAndFirstLetterFamilyName',
                      ),
                    },
                  ]}
                />
              )}
            />
          </FormGroup>
        )}
        {groups.length > 1 && (
          <FilterGroupsEleves
            groups={groups}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
          />
        )}
      </SidebarWithSections.Section>
      <SidebarWithSections.Section
        icon={SwatchIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="fontSize"
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector
                min={8}
                max={20}
                onChange={onChange}
                value={value}
              />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                limitedBgSet={limitedBgSet}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FormGroup>
        <FormGroup label={t('eleve:pyramide.girlsColorLabel')}>
          <Field
            name="girlsColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('eleve:pyramide.boysColorLabel')}>
          <Field
            name="boysColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('eleve:pyramide.neutralColorLabel')}>
          <Field
            name="neutralColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper />
        </FormGroup>
        <Field
          name="withPagination"
          type="checkbox"
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
